import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { IPaginatedResponse } from "../../@types/PaginatedResponse";
import {
  IPromsTableData,
  ISurveyMaxScore,
  ISurveyPromsTableData,
  ISurveysForms,
} from "../../@types/Proms";
import { DEFAULT_PAGE_SIZE } from "../../constants/pagination";
import { promsService } from "../../services/promsService";
import { getLongFormattedDate } from "../../utils/dateFormatter";

function roundScoreToOneDecimal(score: number) {
  return Math.round(score * 10) / 10;
}

export function getPatientSurveyFormsData(
  patientId: number,
  surveyId: number,
  fromDate: string,
  toDate: string
) {
  const { data, isFetching, isError, refetch, isRefetching } = useQuery<
    { surveyForms: ISurveysForms[]; maxScores: ISurveyMaxScore[] } | undefined
  >({
    queryKey: [`patient-survey-forms-${patientId}-${surveyId}`, patientId],
    queryFn: async () => {
      if (!patientId || !surveyId) return undefined;

      const { data } = await promsService.getPatientSurveyForms(
        patientId,
        surveyId,
        fromDate,
        toDate
      );

      return {
        surveyForms: (data.surveyForms ?? []) as ISurveysForms[],
        maxScores: data.maxScores ?? [],
      };
    },
  });

  return {
    surveyForms: data?.surveyForms?.map((formsData) => {
      return {
        ...formsData,
        time: formsData.time,
      } as ISurveyPromsTableData;
    }),
    maxScores: data?.maxScores,
    isFetching,
    isError,
    refetch,
    isRefetching,
  };
}

type PromsPaginatedResponse = IPaginatedResponse<IPromsTableData[]>;

/**
 * Hook for fetching patient proms/surveys with infinite scrolling support.
 * Uses TanStack Query's useInfiniteQuery for paginated data fetching.
 *
 * @param patientId - The patient ID to fetch proms for
 * @param pageSize - Number of items per page (default: 20)
 * @param surveyType - Optional filter for survey type ("Patient" or "Clinician")
 */
export function useInfiniteProms(
  patientId: number,
  pageSize: number = DEFAULT_PAGE_SIZE,
  surveyType: "Patient" | "Clinician" | undefined = undefined
) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: ["patient-proms-infinite", patientId, pageSize, surveyType],
    queryFn: async ({ pageParam }): Promise<PromsPaginatedResponse> => {
      if (!patientId) {
        return {
          result: [],
          currentPage: 1,
          pageSize,
          pages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        };
      }

      const response = await promsService.getPatientPromsPaginated(
        patientId,
        pageParam,
        pageSize,
        surveyType
      );

      return response.data as PromsPaginatedResponse;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: PromsPaginatedResponse) => {
      if (lastPage.hasNextPage) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
  });

  const getLastCompletedDate = (lastCompleted: string | undefined) => {
    if (lastCompleted) return dayjs(lastCompleted).toDate();
    return 0;
  };

  // Flatten all pages into a single array of proms
  const proms =
    data?.pages.flatMap((page: PromsPaginatedResponse) => {
      const items = page.result || [];
      return items.map((promsData: IPromsTableData) => {
        const lastcompletedDateString = getLongFormattedDate(
          promsData.lastCompleted
        );
        return {
          ...promsData,
          reminders: promsData.reminders ? "Active" : "Inactive",
          adherence: `${Math.round(Number(promsData.adherence) ?? 0)}%`,
          assigned: getLongFormattedDate(promsData.assigned),
          frequency: promsData.frequency ?? "",
          surveyResult: "",
          lastTotalScore:
            promsData.lastTotalScore !== undefined
              ? roundScoreToOneDecimal(Number(promsData.lastTotalScore))
              : "",
          lastCompleted: lastcompletedDateString,
          lastCompletedDate: getLastCompletedDate(promsData.lastCompleted),
        } as IPromsTableData;
      });
    }) || [];

  // Get total count from the first page
  const totalCount =
    data?.pages[0]?.pages !== undefined
      ? data.pages[0].pages * pageSize
      : proms.length;

  return {
    proms,
    totalCount,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  };
}
